import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  Skeleton,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useTranslation } from "i18n-fe";
import { useNavigate, useParams } from "react-router-dom";
import { useOrganizations } from "../hooks/useOrganizations";
import { PageLayout, StyledBreadcrumb } from "common-fe";
import { extractErrorMessage } from "../utils/errorUtils";

export default function OrganizationEdit() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { id } = useParams();
  const { getOrganization, updateOrganization, createOrganization } = useOrganizations();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [organization, setOrganization] = useState(null);

  const isEdit = id !== "new";
  const pageTitle = isEdit 
    ? t("organizations.createForm.editOrganizationTitle")
    : t("organizations.createForm.createOrganizationTitle");

  // Load organization data for editing
  useEffect(() => {
    const loadOrganization = async () => {
      if (!isEdit) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const orgData = await getOrganization(id);
        setOrganization(orgData);
        
        // Check if user is the owner - if not, redirect to organizations list
        if (!orgData.owner) {
          setSubmitError(t("organizations.notOwnerError"));
          setTimeout(() => {
            navigate("/orgs");
          }, 2000);
          return;
        }
        
        setFormData({
          name: orgData.name || "",
          active: orgData.active !== undefined ? orgData.active : true,
        });
      } catch (err) {
        console.error("Failed to load organization:", err);
        setSubmitError(extractErrorMessage(err, t));
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, [id, isEdit, getOrganization, t, navigate]);

  // Cleanup effect to clear success message on unmount
  useEffect(() => {
    return () => {
      setSuccessMessage("");
    };
  }, []);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t("organizations.createForm.nameRequiredError");
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("organizations.createForm.nameMinLengthError");
    } else if (formData.name.trim().length > 100) {
      newErrors.name = t("organizations.createForm.nameMaxLengthError");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError(null);

    const fd = new FormData(event.currentTarget);
    const name = (fd.get("name") || "").toString().trim();
    const descriptionRaw = fd.get("description");
    const description = descriptionRaw ? descriptionRaw.toString().trim() : undefined;
    const trimmedData = description ? { name, description } : { name };

    if (!trimmedData.name) {
      setSubmitError(t("organizations.nameRequired"));
      setSaving(false);
      return;
    }

    try {
      let promise;
      if (isEdit) {
        promise = updateOrganization(id, trimmedData);
      } else {
        promise = createOrganization(trimmedData);
      }

      await promise;
      setSuccessMessage(t(isEdit ? "organizations.updateSuccess" : "organizations.createSuccess"));
      setTimeout(() => {
        navigate("/orgs");
      }, 2000);

    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError(extractErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    // Clear success message when navigating away
    setSuccessMessage("");
    navigate("/orgs");
  };

  // Create breadcrumbs
  const breadcrumbs = [
    <StyledBreadcrumb
      key="organizations"
      component="a"
      href="/orgs"
      label={t("organizations.organizationsPageTitle")}
      onClick={(e) => {
        e.preventDefault();
        handleBack();
      }}
    />,
    <StyledBreadcrumb
      key="current"
      label={pageTitle}
    />
  ];

  if (loading) {
    return (
      <PageLayout
        breadcrumbs={breadcrumbs}
        onBack={handleBack}
      >
        <Paper elevation={1} sx={{ p: 4 }}>
          <Skeleton variant="text" width={300} height={32} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </Paper>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbs={breadcrumbs}
      onBack={handleBack}
    >
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 4 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label={t("organizations.createForm.nameLabel")}
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            sx={{ mb: 3 }}
            disabled={saving}
          />

          {isEdit && (
            <>
              <Divider sx={{ my: 3 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleChange}
                    name="active"
                    disabled={saving}
                  />
                }
                label={t("organizations.createForm.activeLabel")}
              />
            </>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              type="submit"
              disabled={saving}
              size="large"
            >
              {saving
                ? t("common.saving")
                : isEdit
                ? t("common.save")
                : t("common.create")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </PageLayout>
  );
} 